import {
  BranchQuoteSupportItemType,
  BranchQuoteSupportStatus,
  SupportQuoteItemLinkDetail
} from '../../api/dealer-api-interface-franchisee';
// Passive new ticket component (no direct create/save actions)
import { customElement, property, query, state } from 'lit/decorators.js';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { Editor } from '@toast-ui/editor';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';
import { getSupportStatusBadge } from './conversation-helper';
import { html, PropertyValueMap } from 'lit';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { newStandardEditor } from '../../../webmodule-common/other/ui/toastui';
import { LangBranchQuoteSupportNew } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-new-lang';
import { WebModuleConversationEntryDropZone } from './wm-conversation-entry-dropzone';
import { QuoteItemSelectorModal } from '../quotes/views/franchisee-quote-item-table';
import { deriveSupportCapabilities, renderSupportDebugBanner } from './support-capabilities';
import { SupportTicketService } from './support-ticket-service';
// support-events deprecated for draft propagation (snapshot subscription in service)
// Legacy guard removed; direct dispatch used.

// Removed SupportNewActions (actions handled by higher level orchestrator)

@customElement('wmview-branchquote-support-new')
export class WebModuleBranchQuoteSupportNew extends LitElementBase {
  protected dataBinding = new DataBinding(this);
  protected dataTracker = new DataTracker(this.dataBinding);

  @property({ attribute: false }) quoteManager?: FranchiseeQuoteContainerManager; // object ref - no attribute

  get quoteId(): string | undefined {
    return this.quoteManager?.branchQuote.id;
  }
  get quoteSetId(): string | undefined {
    return this.quoteManager?.branchQuote.quoteSetId;
  }
  @property({ type: Number }) supportType: BranchQuoteSupportItemType =
    BranchQuoteSupportItemType.QuoteAssistanceRequest;
  @state()
  isUploading = false;
  // Buttons removed; higher-level UI issues create/issue commands

  @property()
  public get text() {
    return this.editor?.getMarkdown() ?? '';
  }
  // Deprecated local branchQuoteSupportId; use draft ticket id from service session instead
  // Removed standalone conversationEntryId; always derive from service session draftEntry
  // New session-centric draft ticket id (created via service.createSupportTicketDraft)
  @state() private _draftTicketId: string | null = null;
  /** Optional externally provided draft ticket id (from parent editor view). If supplied, component will NOT create its own draft session. */
  @property({ type: String }) draftTicketId?: string;

  @property() subjectField?: string;

  @property() supplierName = '';

  textEditor: HTMLElement = document.createElement('div');
  @query('#attachments')
  attachmentsEditor?: WebModuleConversationEntryDropZone;
  protected editor?: Editor;

  constructor() {
    super();
    this.prepareBindings();
  }

  prepareBindings() {
    this.dataTracker.addObjectBinding(() => this, 'subjectField', 'subjectField', FieldType.string, true);
  }

  get subject() {
    if (this.dataTracker.modified) this.dataTracker.applyChangeToValue();
    return this.subjectField ?? '';
  }

  @state()
  protected linkedQuoteItemKeys: string[] = [];
  makeDisplayValueForQuoteItemId(id: string) {
    return this.quoteManager?.makeSupportLinkDisplayValueForQuoteItemId(id) ?? '';
  }

  protected getQuoteItemLinks(): Record<string, SupportQuoteItemLinkDetail> {
    return this.linkedQuoteItemKeys.reduce<Record<string, SupportQuoteItemLinkDetail>>((acc, key) => {
      acc[key] = { isLinked: true, id: key, textDisplay: this.makeDisplayValueForQuoteItemId(key) };
      return acc;
    }, {});
  }

  // Input construction delegated to service draft state (removed local build function usage)
  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (this.textEditor && !this.editor) {
      this.editor = newStandardEditor(this.textEditor, undefined, 275);
      (this.editor as any).on('change', () => this.syncDraftToService());
    }
  }
  protected eventCancelEntry = async (e: CustomEvent) => {
    e.stopImmediatePropagation();
  };

  // Validation delegated entirely to service (validateDraftSession)

  // Creation logic removed; passive draft updates only
  public async save(): Promise<boolean> {
    return true;
  }

  connectedCallback(): void {
    super.connectedCallback();
    // this.subjectEditor?.focus();
  }

  get forceReadonly() {
    return this.quoteManager?.isReadonly() ?? true;
  }

  render() {
    const evalResult = deriveSupportCapabilities({
      quoteManager: this.quoteManager,
      ticketStatus: undefined,
      forceReadonly: this.forceReadonly,
      transientBusy: this.isUploading,
      phase: 'new'
    });
    const readonly = evalResult.readonly || this.forceReadonly; // legacy forceReadonly still respected
    const forms = new FormInputAssistant(this.dataTracker, readonly);
    const debugBanner = renderSupportDebugBanner(evalResult, 'New Ticket');

    const mainHeader = html`<div
      class="main-header d-flex justify-content-between  border-bottom border-secondary mb-2"
    >
      <h2>${LangBranchQuoteSupportNew.supportRequest()} ${getSupportStatusBadge(BranchQuoteSupportStatus.Draft)}</h2>
    </div> `;

    return html`
      <div class="new-support-ticket form-two-col">
        ${debugBanner}
        <div class="row">
          <div class="eto-left form-column2">
            ${mainHeader}
            <div class="conversation conversation-entry-create">
              <div class="conversation-header mb-3">
                <div class="d-flex conversation-subject-wrapper">
                  ${forms.text1('subjectField', LangBranchQuoteSupportNew.supportSubject(), { maxLength: 100 })}
                </div>
                <div class="d-flex conversation-link-wrapper">
                  ${forms.multiEditRow({
                    title: LangBranchQuoteSupportNew.linkQuoteItems(),
                    toolTip: LangBranchQuoteSupportNew.linkQuoteItemsLabel(),
                    slotted: html`<webmodule-button
                      size="small"
                      variant="primary"
                      @click=${this.showOrSelectLinkedItems}
                    >
                      ${LangBranchQuoteSupportNew.linkQuoteItems()}
                    </webmodule-button>`
                  })}
                </div>
              </div>
              ${this.conversationNoteHeader()} ${this.addNoteTemplate()} ${this.attachmentsTemplate()}
            </div>
          </div>
          <div class="eto-right form-column">
            <!-- Empty, to mimic structure of editor page -->
          </div>
        </div>
      </div>
    `;
  }
  conversationNoteHeader(): unknown {
    const title = LangBranchQuoteSupportNew.supportInitialTopic();

    return html`<div class="pb-1 mb-1 mt-2 border-bottom border-secondary">
      <h2 class="text-primary">${title}</h2>
    </div>`;
  }

  protected eventUploadingChanged = (e: CustomEvent<{ isUploading: boolean }>) => {
    this.isUploading = e.detail.isUploading;
    this.dispatchCustom('changed', { isUploading: this.isUploading });
    // Snapshot publish from service covers draft change.
  };
  attachmentsTemplate(): unknown {
    const conversationId = this.draftTicketIdResolved;
    return html`<wmview-conversation-attachments
      id="attachments"
      @wm-event-changed=${this.eventUploadingChanged}
      .conversationId=${conversationId}
      .conversationEntryId=${this.draftConversationEntryId}
    ></wmview-conversation-attachments>`;
  }

  footerTemplate(): unknown {
    return html``;
  }
  addNoteTemplate(): unknown {
    return this.textEditor;
  }
  private ensureDraftInitialized() {
    if (!this.quoteId || !this.quoteManager) return;
    const service: SupportTicketService = this.quoteManager.supportTicketService;
    // Prefer externally supplied id
    if (this.draftTicketId && this._draftTicketId !== this.draftTicketId) {
      this._draftTicketId = this.draftTicketId;
      return;
    }
    if (!this._draftTicketId) {
      // Only create if no external id provided
      this._draftTicketId = service.createSupportTicketDraft({
        quoteId: this.quoteId,
        supportType: this.supportType,
        supplierName: this.supplierName
      });
    }
  }
  private get draftTicketIdResolved(): string {
    // Ensure a draft session exists so we have a stable ticket id for attachments
    this.ensureDraftInitialized();
    return this._draftTicketId || '';
  }
  /**
   * Public method to flush current UI field values (subject, text, links, attachments)
   * into the centralized SupportTicketService new-ticket draft. Safe to call repeatedly.
   */
  public syncDraftToService() {
    if (!this.quoteManager || !this.quoteId) return;
    this.ensureDraftInitialized();
    const service: SupportTicketService = this.quoteManager.supportTicketService;
    if (!this._draftTicketId) return; // draft not yet initialized
    // Ensure draftEntry exists before pushing updates so attachments use stable id
    try {
      const session = service.getSession(this._draftTicketId);
      if (!session.draftEntry) session.ensureDraftEntry(() => newGuid());
    } catch {
      /* ignore */
    }
    service.updateDraftSession(this._draftTicketId, {
      subject: this.subject,
      text: this.text,
      quoteItemLinks: this.getQuoteItemLinks(),
      attachments: this.attachmentsEditor?.allFilesAsInputs ?? []
    });
    // draft change handled by snapshot publish
  }
  private get draftConversationEntryId(): string {
    if (!this.quoteManager || !this._draftTicketId) return '';
    try {
      const svc = this.quoteManager.supportTicketService;
      const session = svc.getSession(this._draftTicketId);
      if (!session.draftEntry) session.ensureDraftEntry(() => newGuid());
      return session.draftEntry?.id || '';
    } catch {
      return '';
    }
  }
  protected updated(changed: Map<string, unknown>): void {
    // If parent provided a draftTicketId after first render, adopt it
    if (changed.has('draftTicketId') && this.draftTicketId) {
      this._draftTicketId = this.draftTicketId;
      // Avoid creating a second session; do not call ensureDraftInitialized here.
    }
    if (changed.has('subjectField')) this.syncDraftToService();
  }
  async showOrSelectLinkedItems() {
    if (!this.quoteManager) return;
    const modal = new QuoteItemSelectorModal(this.quoteManager!, this.linkedQuoteItemKeys);
    await modal.showModal();
    if (modal.result?.success) {
      this.linkedQuoteItemKeys = modal.result.keys;
      // propagate link changes to service draft
      const service: SupportTicketService = this.quoteManager!.supportTicketService;
      if (this.quoteId) {
        this.ensureDraftInitialized();
        if (this._draftTicketId) {
          service.updateDraftSession(this._draftTicketId, { quoteItemLinks: this.getQuoteItemLinks() });
        }
        // draft change handled by snapshot publish
      }
    }
  }
}
